import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowUp, ArrowDown, ChevronsUp, Loader2, Plus, Save,
  Trash2, Upload, Eye, EyeOff, Package, Mail,
} from "lucide-react";
import { api, API, formatApiErrorDetail } from "../lib/api";

const uid = () => Math.random().toString(36).slice(2, 12);

const emptyVariant = () => ({
  id: uid(),
  pn: "",
  label: "",
  price: 20,
  weight_grams: 100,
  mockup: "",
  note: "",
});

const emptyDraft = () => ({
  title: "",
  description: "",
  cover_image: "",
  gallery: [],
  design_front: "",
  design_back: "",
  variants: [emptyVariant()],
  active: true,
  enquire_only: false,
});

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/uploads`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!r.ok) throw new Error("Upload failed");
  const data = await r.json();
  return data.url;
}

export default function AdminInkShop() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(null); // null = list view; object = editing
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);
  const [toggling, setToggling] = useState(false);

  const refresh = async () => {
    try {
      const [products, checkout] = await Promise.all([
        api.get("/admin/ink-products").then((r) => r.data),
        api.get("/settings/ink-shop-checkout").then((r) => r.data).catch(() => ({ enabled: false })),
      ]);
      setItems(products);
      setCheckoutEnabled(!!checkout.enabled);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggleCheckout = async () => {
    setToggling(true);
    setError("");
    try {
      const next = !checkoutEnabled;
      await api.put("/settings/ink-shop-checkout", { enabled: next });
      setCheckoutEnabled(next);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setToggling(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product? This can't be undone.")) return;
    try {
      await api.delete(`/admin/ink-products/${id}`);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const move = async (idx, delta) => {
    if (!items) return;
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved);
    setItems(next);
    try {
      await api.post("/admin/ink-products/reorder", { ids: next.map((p) => p.id) });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await refresh();
    }
  };

  const moveToTop = async (idx) => {
    if (!items || idx <= 0) return;
    const next = [...items];
    const [moved] = next.splice(idx, 1);
    next.unshift(moved);
    setItems(next);
    try {
      await api.post("/admin/ink-products/reorder", { ids: next.map((p) => p.id) });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      await refresh();
    }
  };

  const startNew = () => setDraft(emptyDraft());
  const startEdit = (p) => setDraft({ ...p, gallery: p.gallery || [], variants: p.variants || [] });

  const saveDraft = async () => {
    setError("");
    setCreating(true);
    try {
      const payload = {
        title: draft.title,
        description: draft.description,
        cover_image: draft.cover_image,
        gallery: draft.gallery,
        design_front: draft.design_front,
        design_back: draft.design_back || null,
        variants: draft.variants,
        active: draft.active,
        enquire_only: !!draft.enquire_only,
      };
      if (draft.id) {
        await api.put(`/admin/ink-products/${draft.id}`, payload);
      } else {
        await api.post("/admin/ink-products", payload);
      }
      setDraft(null);
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-[var(--brand-ink)]" data-testid="admin-ink-shop">
      <main className="mx-auto max-w-6xl px-4 py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[var(--brand-body)] hover:text-[var(--brand-ink)]" data-testid="ink-shop-back">
              <ArrowLeft size={14} /> Back to admin
            </Link>
            <span className="text-black/20">·</span>
            <h1 className="font-display text-2xl font-black">Ink shop (Inkthreadable)</h1>
          </div>
          {!draft ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleCheckout}
                disabled={toggling}
                data-testid="ink-shop-checkout-toggle"
                title={checkoutEnabled
                  ? "Live checkout is ON — customers can pay via Stripe"
                  : "Live checkout is OFF — customers see the 'Checkout back soon' message"}
                className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 font-display text-xs font-semibold ${
                  checkoutEnabled
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                    : "border-amber-300 bg-amber-50 text-amber-800"
                }`}
              >
                {toggling ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : checkoutEnabled ? (
                  <Eye size={12} />
                ) : (
                  <EyeOff size={12} />
                )}
                Checkout: {checkoutEnabled ? "ON" : "OFF"}
              </button>
              <button
                type="button"
                onClick={startNew}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-xs font-semibold text-white hover:opacity-90"
                data-testid="ink-shop-new"
              >
                <Plus size={12} /> New product
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 font-display text-xs font-semibold hover:border-[#0A0F17]"
              data-testid="ink-shop-cancel"
            >
              Cancel
            </button>
          )}
        </div>

        {error ? (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="ink-shop-error">
            {error}
          </div>
        ) : null}

        {draft ? (
          <DraftEditor
            draft={draft}
            setDraft={setDraft}
            onSave={saveDraft}
            saving={creating}
          />
        ) : items === null ? (
          <div className="rounded-md border border-black/10 bg-white p-6 text-center text-sm text-[var(--brand-body)]">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-black/20 bg-white p-8 text-center" data-testid="ink-shop-empty">
            <Package className="mx-auto h-6 w-6 text-[var(--brand-body)]" />
            <p className="mt-2 text-sm text-[var(--brand-body)]">
              No products yet. Click <strong>New product</strong> to add your first Inkthreadable design.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="ink-shop-list">
            {items.map((p, idx) => (
              <li
                key={p.id}
                className={`flex flex-col gap-3 rounded-md border bg-white p-4 sm:flex-row sm:items-start sm:justify-between ${p.active ? "border-black/10" : "border-dashed border-black/20 opacity-70"}`}
                data-testid={`ink-shop-item-${p.id}`}
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  {p.cover_image ? (
                    <img
                      src={p.cover_image}
                      alt=""
                      className="h-16 w-16 shrink-0 rounded-md object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 shrink-0 rounded-md bg-black/5" />
                  )}
                  <div className="min-w-0">
                    <p className="font-display text-base font-bold">
                      {p.title}
                      {p.enquire_only ? (
                        <span
                          className="ml-2 inline-flex items-center gap-1 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 align-middle"
                          data-testid={`ink-shop-enquire-badge-${p.id}`}
                        >
                          <Mail size={10} /> Enquire
                        </span>
                      ) : null}
                    </p>
                    <p className="mt-0.5 text-xs text-[var(--brand-body)]">
                      {p.variants.length} variant{p.variants.length === 1 ? "" : "s"}
                      {" · "}
                      from £{Math.min(...p.variants.map((v) => v.price)).toFixed(2)}
                      {!p.active ? " · Hidden" : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button type="button" onClick={() => moveToTop(idx)} disabled={idx === 0} title="Move to top" data-testid={`ink-shop-top-${p.id}`} className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-40">
                    <ChevronsUp size={12} />
                  </button>
                  <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up" data-testid={`ink-shop-up-${p.id}`} className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-40">
                    <ArrowUp size={12} />
                  </button>
                  <button type="button" onClick={() => move(idx, +1)} disabled={idx === items.length - 1} title="Move down" data-testid={`ink-shop-down-${p.id}`} className="rounded-md border border-black/10 p-1.5 hover:border-[#0A0F17] disabled:opacity-40">
                    <ArrowDown size={12} />
                  </button>
                  <button type="button" onClick={() => startEdit(p)} className="rounded-md border border-black/10 px-2.5 py-1.5 font-display text-xs font-semibold hover:border-[#0A0F17]" data-testid={`ink-shop-edit-${p.id}`}>
                    Edit
                  </button>
                  <button type="button" onClick={() => remove(p.id)} title="Delete" data-testid={`ink-shop-delete-${p.id}`} className="rounded-md border border-red-200 p-1.5 text-red-600 hover:bg-red-50">
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}


function DraftEditor({ draft, setDraft, onSave, saving }) {
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const setVariant = (idx, key, val) =>
    setDraft((d) => {
      const next = [...d.variants];
      next[idx] = { ...next[idx], [key]: val };
      return { ...d, variants: next };
    });
  const addVariant = () => setDraft((d) => ({ ...d, variants: [...d.variants, emptyVariant()] }));
  const removeVariant = (idx) =>
    setDraft((d) => {
      if (d.variants.length <= 1) return d;
      const next = [...d.variants];
      next.splice(idx, 1);
      return { ...d, variants: next };
    });

  const [uploading, setUploading] = useState(null);
  const uploadFor = async (which, file, idx) => {
    if (!file) return;
    setUploading(which);
    try {
      const url = await uploadImage(file);
      if (which === "cover") set("cover_image")(url);
      else if (which === "front") set("design_front")(url);
      else if (which === "back") set("design_back")(url);
      else if (which === "gallery") set("gallery")([...(draft.gallery || []), url]);
      else if (which === "mockup") setVariant(idx, "mockup", url);
    } catch (_e) {
      // errors surfaced via saveDraft — keeping this quiet
    } finally {
      setUploading(null);
    }
  };

  return (
    <div className="space-y-6 rounded-md border border-black/10 bg-white p-6" data-testid="ink-shop-draft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold">
          {draft.id ? "Edit product" : "New product"}
        </h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => set("enquire_only")(!draft.enquire_only)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold ${
              draft.enquire_only
                ? "border-amber-300 bg-amber-50 text-amber-800"
                : "border-black/15 text-[var(--brand-body)]"
            }`}
            title="When ON, the public product page shows an 'Enquire to buy' button instead of Add-to-Cart. No Stripe / Inkthreadable API is used — enquiries land in your inbox."
            data-testid="ink-shop-enquire-only"
          >
            <Mail size={12} />
            {draft.enquire_only ? "Enquire-only (no cart)" : "Cart + Stripe"}
          </button>
          <button
            type="button"
            onClick={() => set("active")(!draft.active)}
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold ${
              draft.active
                ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                : "border-black/15 text-[var(--brand-body)]"
            }`}
            data-testid="ink-shop-active"
          >
            {draft.active ? <Eye size={12} /> : <EyeOff size={12} />}
            {draft.active ? "Visible in /shop" : "Hidden"}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
            data-testid="ink-shop-save"
          >
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save size={12} />} Save
          </button>
        </div>
      </div>

      <Field label="Title *">
        <input type="text" value={draft.title} onChange={(e) => set("title")(e.target.value)} placeholder="LAST · Sky Blue Elephant Tee" className="w-full rounded-sm border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#0A0F17]" data-testid="ink-shop-title" />
      </Field>

      <Field label="Description">
        <textarea rows={4} value={draft.description} onChange={(e) => set("description")(e.target.value)} placeholder="Short description shown on the product page." className="w-full rounded-sm border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#0A0F17]" data-testid="ink-shop-description" />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <ImageField label="Cover image *" value={draft.cover_image} onUrl={set("cover_image")} onFile={(f) => uploadFor("cover", f)} uploading={uploading === "cover"} testid="ink-shop-cover" />
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">Gallery (up to 10)</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {(draft.gallery || []).map((g, i) => (
              <div key={i} className="relative">
                <img src={g} alt="" className="h-16 w-16 rounded-md object-cover" />
                <button type="button" onClick={() => set("gallery")(draft.gallery.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 rounded-full bg-red-600 p-0.5 text-white shadow" title="Remove">
                  <Trash2 size={10} />
                </button>
              </div>
            ))}
            {(draft.gallery || []).length < 10 ? (
              <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-black/25 text-[var(--brand-body)] hover:border-[#0A0F17]">
                {uploading === "gallery" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload size={14} />}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFor("gallery", e.target.files?.[0])} />
              </label>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ImageField label="Design URL — front *" value={draft.design_front} onUrl={set("design_front")} onFile={(f) => uploadFor("front", f)} uploading={uploading === "front"} testid="ink-shop-design-front" help="Publicly-accessible URL of the print file Inkthreadable will use." />
        <ImageField label="Design URL — back (optional)" value={draft.design_back} onUrl={set("design_back")} onFile={(f) => uploadFor("back", f)} uploading={uploading === "back"} testid="ink-shop-design-back" />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
            Variants — one row per size / colour with its Inkthreadable product code
          </label>
          <button type="button" onClick={addVariant} className="inline-flex items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs hover:border-[#0A0F17]" data-testid="ink-shop-add-variant">
            <Plus size={10} /> Add variant
          </button>
        </div>
        <p className="mt-1 text-[10px] text-[var(--brand-body)]">
          Weight guide from Inkthreadable: T-shirt 100 · Hoodie 200 · Mug 150 · Cap 200 · Tote 100 · Beanie 100 · Sticker/Card 50.
        </p>
        <div className="mt-2 space-y-2">
          {draft.variants.map((v, idx) => (
            <div key={v.id} className="grid grid-cols-12 gap-2 rounded-md border border-black/10 p-3" data-testid={`ink-shop-variant-${idx}`}>
              <input type="text" value={v.pn} onChange={(e) => setVariant(idx, "pn", e.target.value)} placeholder="pn (e.g. JH001-S-SKY)" className="col-span-3 rounded-sm border border-black/15 px-2 py-1.5 text-xs" data-testid={`ink-shop-variant-pn-${idx}`} />
              <input type="text" value={v.label} onChange={(e) => setVariant(idx, "label", e.target.value)} placeholder="Label (e.g. Small · Sky Blue)" className="col-span-3 rounded-sm border border-black/15 px-2 py-1.5 text-xs" data-testid={`ink-shop-variant-label-${idx}`} />
              <div className="col-span-2 flex items-center gap-1">
                <span className="text-xs text-[var(--brand-body)]">£</span>
                <input type="number" step="0.01" min="0" value={v.price} onChange={(e) => setVariant(idx, "price", parseFloat(e.target.value) || 0)} className="w-full rounded-sm border border-black/15 px-2 py-1.5 text-xs" data-testid={`ink-shop-variant-price-${idx}`} />
              </div>
              <div className="col-span-2 flex items-center gap-1" title="Parcel weight in grams — used to compute shipping. T-shirt 100 · Hoodie 200 · Mug 150 · Cap 200.">
                <input type="number" step="10" min="1" value={v.weight_grams ?? 100} onChange={(e) => setVariant(idx, "weight_grams", parseInt(e.target.value, 10) || 100)} className="w-full rounded-sm border border-black/15 px-2 py-1.5 text-xs" data-testid={`ink-shop-variant-weight-${idx}`} />
                <span className="text-xs text-[var(--brand-body)]">g</span>
              </div>
              <input type="text" value={v.note || ""} onChange={(e) => setVariant(idx, "note", e.target.value)} placeholder="Note (admin only)" className="col-span-1 rounded-sm border border-black/15 px-2 py-1.5 text-xs" />
              <button type="button" onClick={() => removeVariant(idx)} disabled={draft.variants.length <= 1} className="col-span-1 rounded-md border border-red-200 p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-30" title="Remove variant" data-testid={`ink-shop-variant-remove-${idx}`}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}


function ImageField({ label, value, onUrl, onFile, uploading, testid, help }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">{label}</label>
      <div className="mt-1 flex flex-wrap items-start gap-2">
        {value ? (
          <img src={value} alt="" className="h-16 w-16 rounded-md object-cover" />
        ) : null}
        <div className="min-w-0 flex-1">
          <input type="text" value={value || ""} onChange={(e) => onUrl(e.target.value)} placeholder="https://..." className="w-full rounded-sm border border-black/15 px-2 py-1.5 text-xs" data-testid={testid} />
          <label className="mt-1 inline-flex cursor-pointer items-center gap-1 rounded-md border border-black/15 px-2 py-1 text-xs hover:border-[#0A0F17]">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload size={10} />} Upload
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          {help ? <p className="mt-1 text-[10px] text-[var(--brand-body)]">{help}</p> : null}
        </div>
      </div>
    </div>
  );
}
